import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadShowFinale, saveShowFinale, uploadAttachment, isUploadConfigured } from '../integrations/quizApiAdapter';
import resolveAssetUrl from '../utils/assets';
import Button from './ui/Button';
import FinaleQuizpokerRegie from './FinaleQuizpokerRegie';
import './css/FinaleQuizpokerView.css';

const DEFAULT_CONFIG = {
    blindMode: 'ante',
    anteAmount: 1,
    smallBlind: 5,
    bigBlind: 10,
    potMode: 'simple',
    minRaise: 1,
};

const CLUE_COUNT = 3;

function makeEmptyHand(index) {
    return {
        id: `hand-${Date.now()}-${index}`,
        question: '',
        answerValue: '',
        unit: '',
        clues: ['', '', ''],
        attachments: [],
    };
}

function normalizeLoadedHand(hand, index) {
    const clues = Array.isArray(hand?.clues) ? hand.clues.slice(0, CLUE_COUNT) : [];
    while (clues.length < CLUE_COUNT) clues.push('');
    return {
        id: typeof hand?.id === 'string' && hand.id ? hand.id : `hand-${index + 1}`,
        question: hand?.question ?? '',
        answerValue: typeof hand?.answerValue === 'number' ? hand.answerValue : '',
        unit: hand?.unit ?? '',
        clues,
        attachments: Array.isArray(hand?.attachments) ? hand.attachments : [],
    };
}

function FinaleQuizpokerView({ playsetId }) {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [hands, setHands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveStatus, setSaveStatus] = useState('');
    const [busy, setBusy] = useState(false);
    const [uploadingHandId, setUploadingHandId] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [contentVisible, setContentVisible] = useState(true);

    const load = useCallback(async () => {
        if (!playsetId) {
            setLoading(false);
            setError('Keine aktive Show. Bitte zuerst eine Show aktivieren.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const finale = await loadShowFinale(playsetId);
            const state = finale?.state || {};
            setConfig({ ...DEFAULT_CONFIG, ...(state.config || {}) });
            const loadedHands = Array.isArray(state.hands) ? state.hands.map(normalizeLoadedHand) : [];
            setHands(loadedHands);
        } catch (err) {
            setError(err?.message || 'Fehler beim Laden des Quizpoker-Finales.');
        } finally {
            setLoading(false);
        }
    }, [playsetId]);

    useEffect(() => {
        load();
    }, [load]);

    const buildStatePayload = useCallback(() => {
        return {
            config,
            hands: hands.map((hand) => ({
                id: hand.id,
                question: hand.question,
                answerValue: hand.answerValue === '' || hand.answerValue === null ? null : Number(hand.answerValue),
                unit: hand.unit,
                clues: hand.clues,
                attachments: hand.attachments,
            })),
        };
    }, [config, hands]);

    const persist = useCallback(async (statePayload) => {
        setBusy(true);
        setSaveStatus('');
        try {
            await saveShowFinale(playsetId, { kind: 'quizpoker', state: statePayload });
            setSaveStatus('Gespeichert.');
            setTimeout(() => setSaveStatus(''), 2500);
        } catch (err) {
            setError(err?.message || 'Fehler beim Speichern.');
        } finally {
            setBusy(false);
        }
    }, [playsetId]);

    const handleSave = useCallback(() => {
        persist(buildStatePayload());
    }, [persist, buildStatePayload]);

    const updateHand = useCallback((handId, patch) => {
        setHands((prev) => prev.map((h) => (h.id === handId ? { ...h, ...patch } : h)));
    }, []);

    const updateClue = useCallback((handId, clueIndex, value) => {
        setHands((prev) =>
            prev.map((h) => {
                if (h.id !== handId) return h;
                const clues = [...h.clues];
                clues[clueIndex] = value;
                return { ...h, clues };
            })
        );
    }, []);

    const addHand = useCallback(() => {
        setHands((prev) => [...prev, makeEmptyHand(prev.length)]);
    }, []);

    const removeHand = useCallback((handId) => {
        setHands((prev) => prev.filter((h) => h.id !== handId));
    }, []);

    const moveHand = useCallback((handId, dir) => {
        setHands((prev) => {
            const idx = prev.findIndex((h) => h.id === handId);
            if (idx < 0) return prev;
            const target = idx + dir;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            const [item] = next.splice(idx, 1);
            next.splice(target, 0, item);
            return next;
        });
    }, []);

    const handleImageUpload = useCallback(async (handId, file) => {
        if (!file) return;
        setUploadingHandId(handId);
        setError('');
        try {
            const result = await uploadAttachment(file);
            updateHand(handId, {
                attachments: [
                    {
                        url: result.url,
                        mime: result.mime,
                        originalName: result.originalName,
                        size: result.size,
                        placement: 'above',
                    },
                ],
            });
        } catch (err) {
            setError(err?.message || 'Bild-Upload fehlgeschlagen.');
        } finally {
            setUploadingHandId(null);
        }
    }, [updateHand]);

    const uploadConfigured = useMemo(() => isUploadConfigured(), []);

    if (loading) {
        return <div className="page text-muted">Quizpoker-Finale wird geladen…</div>;
    }

    return (
        <div className="page section-stack qp-edit">
            <FinaleQuizpokerRegie />

            {/* Trennung Regie / Inhalte */}
            <div className="qp-content-bar">
                <h2 className="qp-content-title">Inhalte</h2>
                <button
                    type="button"
                    className={`toggle-button ${contentVisible ? 'is-on' : ''}`}
                    role="switch"
                    aria-checked={contentVisible}
                    onClick={() => setContentVisible((v) => !v)}
                >
                    <span className="toggle-indicator" aria-hidden="true" />
                    <span className="toggle-label">Inhalte anzeigen</span>
                </button>
            </div>
            {error && (
                <div className="error-banner" style={{ background: '#fdecea', color: '#b71c1c', padding: '8px', borderRadius: '4px' }}>
                    {error}
                </div>
            )}

            {/* Fester Einstellungen-Button am Seitenrand (immer erreichbar) */}
            <button type="button" className="qp-settings-fab" onClick={() => setSettingsOpen(true)}>
                Einstellungen
            </button>

            {/* Einstellungen als seitlicher Drawer */}
            {settingsOpen && (
                <div className="qp-settings-overlay" onClick={() => setSettingsOpen(false)}>
                    <aside className="qp-settings-drawer" onClick={(e) => e.stopPropagation()}>
                        <div className="qp-settings-header">
                            <h3 className="card-title">Einstellungen</h3>
                            <Button variant="ghost" size="sm" onClick={() => setSettingsOpen(false)}>Schließen</Button>
                        </div>
                        <div className="qp-settings-body">
                            <div className="input-group">
                                <label>Pflichteinsatz</label>
                                <select
                                    value={config.blindMode}
                                    onChange={(e) => setConfig((c) => ({ ...c, blindMode: e.target.value }))}
                                >
                                    <option value="ante">Ante (alle gleich)</option>
                                    <option value="blinds">Small / Big Blind</option>
                                </select>
                            </div>
                            {config.blindMode === 'ante' ? (
                                <div className="input-group">
                                    <label>Ante</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={config.anteAmount}
                                        onChange={(e) => setConfig((c) => ({ ...c, anteAmount: Number(e.target.value) }))}
                                    />
                                </div>
                            ) : (
                                <>
                                    <div className="input-group">
                                        <label>Small Blind</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={config.smallBlind}
                                            onChange={(e) => setConfig((c) => ({ ...c, smallBlind: Number(e.target.value) }))}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Big Blind</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={config.bigBlind}
                                            onChange={(e) => setConfig((c) => ({ ...c, bigBlind: Number(e.target.value) }))}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="input-group">
                                <label>Pot-Modus</label>
                                <select
                                    value={config.potMode}
                                    onChange={(e) => setConfig((c) => ({ ...c, potMode: e.target.value }))}
                                >
                                    <option value="simple">Vereinfacht (ein Pot)</option>
                                    <option value="sidepots">Side-Pots</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label>Mindest-Erhöhung</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={config.minRaise}
                                    onChange={(e) => setConfig((c) => ({ ...c, minRaise: Number(e.target.value) }))}
                                />
                            </div>
                        </div>
                        <div className="button-row">
                            <Button variant="primary" onClick={() => { handleSave(); setSettingsOpen(false); }}>
                                Speichern &amp; schließen
                            </Button>
                        </div>
                    </aside>
                </div>
            )}

            {/* Hände (ein-/ausblendbar für Moderator-Monitor) */}
            {contentVisible && (
            <div className="card section-stack">
                <div className="card-header">
                    <h3 className="card-title">Fragen ({hands.length})</h3>
                    <Button size="sm" onClick={addHand}>Frage hinzufügen</Button>
                </div>
                {hands.length === 0 && (
                    <div className="text-muted">Noch keine Fragen. Lege die erste Quizpoker-Frage an.</div>
                )}
                <div className="qp-hands">
                    {hands.map((hand, index) => {
                        const image = hand.attachments?.[0] || null;
                        return (
                            <div key={hand.id} className="qp-hand-card">
                                <div className="qp-hand-head">
                                    <span className="qp-hand-no">Frage {index + 1}</span>
                                    <div className="button-row">
                                        <Button size="sm" variant="ghost" onClick={() => moveHand(hand.id, -1)} disabled={index === 0}>↑</Button>
                                        <Button size="sm" variant="ghost" onClick={() => moveHand(hand.id, 1)} disabled={index === hands.length - 1}>↓</Button>
                                        <Button size="sm" variant="danger" onClick={() => removeHand(hand.id)}>Löschen</Button>
                                    </div>
                                </div>
                                <div className="input-group">
                                    <label>Frage</label>
                                    <input
                                        type="text"
                                        value={hand.question}
                                        onChange={(e) => updateHand(hand.id, { question: e.target.value })}
                                        placeholder="z. B. Wie hoch ist der Eiffelturm (in Metern)?"
                                    />
                                </div>
                                <div className="qp-hand-row">
                                    <div className="input-group">
                                        <label>Lösung (Zahl)</label>
                                        <input
                                            type="number"
                                            value={hand.answerValue}
                                            onChange={(e) => updateHand(hand.id, { answerValue: e.target.value })}
                                            placeholder="330"
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>Einheit (optional)</label>
                                        <input
                                            type="text"
                                            value={hand.unit}
                                            onChange={(e) => updateHand(hand.id, { unit: e.target.value })}
                                            placeholder="m"
                                        />
                                    </div>
                                </div>
                                <div className="qp-clues">
                                    {hand.clues.map((clue, clueIndex) => (
                                        <div className="input-group" key={clueIndex}>
                                            <label>Hinweis {clueIndex + 1}</label>
                                            <input
                                                type="text"
                                                value={clue}
                                                onChange={(e) => updateClue(hand.id, clueIndex, e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="qp-hand-image">
                                    <label>Bild (optional)</label>
                                    {image && (
                                        <div className="qp-image-preview">
                                            <img src={resolveAssetUrl(image.url)} alt="" />
                                            <Button size="sm" variant="ghost" onClick={() => updateHand(hand.id, { attachments: [] })}>
                                                Entfernen
                                            </Button>
                                        </div>
                                    )}
                                    {uploadConfigured ? (
                                        <input
                                            type="file"
                                            accept="image/*"
                                            disabled={uploadingHandId === hand.id}
                                            onChange={(e) => handleImageUpload(hand.id, e.target.files?.[0])}
                                        />
                                    ) : (
                                        <span className="text-muted" style={{ fontSize: '0.85em' }}>Upload nicht konfiguriert.</span>
                                    )}
                                    {uploadingHandId === hand.id && <span className="text-muted">Lädt hoch…</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="button-row">
                    <Button variant="primary" onClick={handleSave} disabled={busy || !playsetId}>
                        {busy ? 'Speichere…' : 'Speichern'}
                    </Button>
                    {saveStatus && <span className="text-muted">{saveStatus}</span>}
                </div>
            </div>
            )}
        </div>
    );
}

export default FinaleQuizpokerView;
